import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, share, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { SupportAccessActions } from '@states/support-access/support-access.action-types';

import { UtilsService } from '../../edge/utils.service';
import { SupportAccessService } from '../../services/support-access.service';

@Injectable()
export class SupportAccessEffects {
  public getSupportAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.getSupportAccess),
      switchMap(() => this.supportAccessService.getSupportAccess().pipe(
        switchMap((documents) => [
          SupportAccessActions.getSupportAccessSuccess({ documents }),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.getSupportAccessFail(),
        ]),
        share(),
      )),
    ),
  );

  public getAllSupportAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.getAllSupportAccess),
      switchMap(() => this.supportAccessService.getAllSupportAccess().pipe(
        switchMap((documents) => [
          SupportAccessActions.getAllSupportAccessSuccess({ documents }),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.getAllSupportAccessFail(),
        ]),
        share(),
      )),
    ),
  );

  public createSupportAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.createSupportAccess),
      switchMap(({ body }) => this.supportAccessService.createSupportAccess(body).pipe(
        switchMap(() => [
          SharedActions.showMessage({ success: this.translateService.instant('ACCOUNT.SUPPORT_ACCESS.MESSAGES.CREATED') }),
          SupportAccessActions.createSupportAccessSuccess(),
          SupportAccessActions.getSupportAccess(),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.createSupportAccessFail(),
        ]),
        share(),
      )),
    ),
  );

  public updateSupportAccessEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.updateSupportAccessEnabled),
      switchMap(({ id, body, previousEnabled }) => this.supportAccessService.updateSupportAccessEnabled(id, body).pipe(
        switchMap((document) => [
          SupportAccessActions.updateSupportAccessEnabledSuccess({ document }),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.updateSupportAccessEnabledFail({ id, previousEnabled }),
        ]),
        share(),
      )),
    ),
  );

  public updateSupportAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.updateSupportAccess),
      switchMap(({ id, body }) => this.supportAccessService.updateSupportAccess(id, body).pipe(
        switchMap((document) => [
          SharedActions.showMessage({ success: this.translateService.instant('ACCOUNT.SUPPORT_ACCESS.MESSAGES.UPDATED') }),
          SupportAccessActions.updateSupportAccessSuccess({ document }),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.updateSupportAccessFail(),
        ]),
        share(),
      )),
    ),
  );

  public revertUpdateSupportAccessEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.updateSupportAccessEnabledFail),
      switchMap(({ id, previousEnabled }) => [
        SupportAccessActions.updateSupportAccessStore({
          id,
          document: { enabled: previousEnabled },
        }),
      ]),
    ),
  );

  public deleteSupportAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupportAccessActions.deleteSupportAccess),
      switchMap(({ id }) => this.supportAccessService.deleteSupportAccess(id).pipe(
        switchMap(() => [
          SharedActions.showMessage({ success: this.translateService.instant('ACCOUNT.SUPPORT_ACCESS.MESSAGES.REVOKED') }),
          SupportAccessActions.deleteSupportAccessSuccess({ id }),
        ]),
        catchError((response) => [
          SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          SupportAccessActions.deleteSupportAccessFail(),
        ]),
        share(),
      )),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly supportAccessService: SupportAccessService,
    private readonly utilsService: UtilsService,
    private readonly translateService: TranslateService,
  ) {}
}
